import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["focusable", "accessibleWhenDisabled", "autoFocus", "onFocusVisible"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useEffect, useState } from 'react';
import { isButton } from 'ariakit-utils/dom';
import { addGlobalEventListener, isPortalEvent, queueBeforeEvent, isSelfTarget, isFocusEventOutside } from 'ariakit-utils/events';
import { isFocusable, focusIfNeeded } from 'ariakit-utils/focus';
import { useEvent, useSafeLayoutEffect, useTagName, useForkRef } from 'ariakit-utils/hooks';
import { queueMicrotask } from 'ariakit-utils/misc';
import { isSafari } from 'ariakit-utils/platform';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
var isSafariBrowser = isSafari();
var alwaysFocusVisibleInputTypes = ["text", "search", "url", "tel", "email", "password", "number", "date", "month", "week", "time", "datetime", "datetime-local"];

function isAlwaysFocusVisible(element) {
  var tagName = element.tagName,
      readOnly = element.readOnly,
      type = element.type;
  if (tagName === "TEXTAREA" && !readOnly) return true;
  if (tagName === "SELECT" && !readOnly) return true;

  if (tagName === "INPUT" && !readOnly) {
    return alwaysFocusVisibleInputTypes.includes(type);
  }

  if (element.isContentEditable) return true;
  return false;
} // See https://github.com/ariakit/ariakit/issues/1257


function isAlwaysFocusVisibleDelayed(element) {
  var role = element.getAttribute("role");
  if (role === "combobox") return true;
  return false;
}

function getLabels(element) {
  if ("labels" in element) {
    return element.labels;
  }

  return null;
}

function isNativeCheckboxOrRadio(element) {
  var tagName = element.tagName.toLowerCase();

  if (tagName === "input" && element.type) {
    return element.type === "radio" || element.type === "checkbox";
  }

  return false;
}

function isNativeTabbable(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea" || tagName === "a";
}

function supportsDisabledAttribute(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea";
}

function getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, tabIndexProp) {
  if (!focusable) {
    return tabIndexProp;
  }

  if (trulyDisabled) {
    if (nativeTabbable && !supportsDisabled) {
      // Anchor, audio and video tags don't support the `disabled` attribute.
      // We must pass tabIndex={-1} so they don't receive focus on tab.
      return -1;
    } // Elements that support the `disabled` attribute don't need tabIndex.


    return;
  }

  if (nativeTabbable) {
    // If the element is enabled and it's natively tabbable, we don't need to
    // specify a tabIndex attribute unless it's explicitly set by the user.
    return tabIndexProp;
  } // If the element is enabled and is not natively tabbable, we have to
  // fallback tabIndex={0}.


  return tabIndexProp || 0;
}

function useDisableEvent(onEvent, disabled) {
  return useEvent(function (event) {
    onEvent == null ? void 0 : onEvent(event);
    if (event.defaultPrevented) return;

    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  });
} // isKeyboardModality should be true by defaault.


var isKeyboardModality = true;

function onGlobalMouseDown(event) {
  var target = event.target;

  if (target && "hasAttribute" in target) {
    // If the target element is already focus-visible, we keep the keyboard
    // modality.
    if (!target.hasAttribute("data-focus-visible")) {
      isKeyboardModality = false;
    }
  }
}

function onGlobalKeyDown(event) {
  if (event.metaKey) return;
  if (event.ctrlKey) return;
  isKeyboardModality = true;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that can be focused.
 * @see https://ariakit.org/components/focusable
 * @example
 * ```jsx
 * const props = useFocusable();
 * <Role {...props}>Focusable</Role>
 * ```
 */


var useFocusable = createHook(function (_ref) {
  var _ref$focusable = _ref.focusable,
      focusable = _ref$focusable === void 0 ? true : _ref$focusable,
      accessibleWhenDisabled = _ref.accessibleWhenDisabled,
      autoFocus = _ref.autoFocus,
      onFocusVisible = _ref.onFocusVisible,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null); // Add global event listeners to determine whether the user is using a
  // keyboard to navigate the site or not.

  useEffect(function () {
    if (!focusable) return;
    addGlobalEventListener("mousedown", onGlobalMouseDown, true);
    addGlobalEventListener("keydown", onGlobalKeyDown, true);
  }, [focusable]); // Safari and Firefox on Apple devices don't focus on checkboxes or radio
  // buttons when their labels are clicked. This effect will make sure the
  // focusable element is focused on label click.

  if (isSafariBrowser) {
    useEffect(function () {
      if (!focusable) return;
      var element = ref.current;
      if (!element) return;
      if (!isNativeCheckboxOrRadio(element)) return;
      var labels = getLabels(element);
      if (!labels) return;

      var onMouseUp = function onMouseUp() {
        return queueMicrotask(function () {
          return element.focus();
        });
      };

      labels.forEach(function (label) {
        return label.addEventListener("mouseup", onMouseUp);
      });
      return function () {
        labels.forEach(function (label) {
          return label.removeEventListener("mouseup", onMouseUp);
        });
      };
    }, [focusable]);
  }

  var disabled = focusable && props.disabled;
  var trulyDisabled = !!disabled && !accessibleWhenDisabled;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focusVisible = _useState2[0],
      setFocusVisible = _useState2[1]; // When the focusable element is disabled, it doesn't trigger a blur event
  // so we can't set focusVisible to false there. Instead, we have to do it
  // here by checking the element's disabled attribute.


  useEffect(function () {
    if (!focusable) return;

    if (trulyDisabled && focusVisible) {
      setFocusVisible(false);
    }
  }, [focusable, trulyDisabled, focusVisible]); // When an element that has focus becomes hidden, it doesn't trigger a blur
  // event so we can't set focusVisible to false there. We observe the element
  // and check if it's still focusable. Otherwise, we set focusVisible to
  // false.

  useEffect(function () {
    if (!focusable) return;
    if (!focusVisible) return;
    var element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") return;
    var observer = new IntersectionObserver(function () {
      if (!isFocusable(element)) {
        setFocusVisible(false);
      }
    });
    observer.observe(element);
    return function () {
      return observer.disconnect();
    };
  }, [focusable, focusVisible]); // Disable events when the element is disabled.

  var onKeyPressCapture = useDisableEvent(props.onKeyPressCapture, disabled);
  var onMouseDownCapture = useDisableEvent(props.onMouseDownCapture, disabled);
  var onClickCapture = useDisableEvent(props.onClickCapture, disabled);
  var onMouseDownProp = props.onMouseDown;
  var onMouseDown = useEvent(function (event) {
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    var element = event.currentTarget; // Safari doesn't focus on buttons on mouse down like other
    // browsers/platforms. Instead, it focuses on the closest focusable
    // ancestor element, which is ultimately the body element. So we make sure
    // to give focus to this Focusable element on mouse down so it works
    // consistently across browsers.

    if (!isSafariBrowser) return;
    if (isPortalEvent(event)) return;
    if (!isButton(element) && !isNativeCheckboxOrRadio(element)) return; // In future versions os Safari, it may change this behavior and start
    // focusing on buttons on mouse down. To account for that, we check if the
    // element has received focus before.

    var receivedFocus = false;

    var onFocus = function onFocus() {
      receivedFocus = true;
    };

    var options = {
      capture: true,
      once: true
    };
    element.addEventListener("focusin", onFocus, options); // We can't focus right away after on mouse down, otherwise it would
    // prevent drag events from happening. So we queue the focus to the next
    // animation frame, but always before the next mouseup event. The mouseup
    // event might happen before the next animation frame on touch devices or
    // by tapping on a MacBook's trackpad, for example.

    queueBeforeEvent(element, "mouseup", function () {
      element.removeEventListener("focusin", onFocus, true);
      if (receivedFocus) return;
      focusIfNeeded(element);
    });
  });
  var onFocusVisibleProp = onFocusVisible;
  var onFocusVisibleEvent = useEvent(function (event) {
    onFocusVisibleProp == null ? void 0 : onFocusVisibleProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    setFocusVisible(true);
  });
  var onKeyDownCaptureProp = props.onKeyDownCapture;
  var onKeyDownCapture = useEvent(function (event) {
    onKeyDownCaptureProp == null ? void 0 : onKeyDownCaptureProp(event);
    if (!focusable) return;
    if (event.metaKey) return;
    if (event.altKey) return;
    if (event.ctrlKey) return;
    if (!isSelfTarget(event)) return;

    if (!focusVisible && !event.defaultPrevented) {
      // Triggers onFocusVisible when the element has initially received
      // non-keyboard focus, but then a key has been pressed.
      onFocusVisibleEvent(event);
    }
  });
  var onFocusCaptureProp = props.onFocusCapture;
  var onFocusCapture = useEvent(function (event) {
    onFocusCaptureProp == null ? void 0 : onFocusCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;

    if (!isSelfTarget(event)) {
      setFocusVisible(false);
      return;
    }

    if (isKeyboardModality || isAlwaysFocusVisible(event.target)) {
      onFocusVisibleEvent(event);
    } // See https://github.com/ariakit/ariakit/issues/1257
    else if (isAlwaysFocusVisibleDelayed(event.target)) {
      queueBeforeEvent(event.target, "focusout", function () {
        return onFocusVisibleEvent(event);
      });
    } else {
      setFocusVisible(false);
    }
  });
  var onBlurProp = props.onBlur; // Note: Can't use onBlurCapture here otherwise it will not work with
  // CompositeItem's with the virtualFocus state set to true.

  var onBlur = useEvent(function (event) {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (!focusable) return;

    if (isFocusEventOutside(event)) {
      setFocusVisible(false);
    }
  }); // The native autoFocus prop is problematic in many ways. For example, when
  // an element has the native autofocus attribute, the focus event will be
  // triggered before React effects (even layout effects) and before refs are
  // assigned. This means we won't have access to the element's ref or
  // anything else that's set up by React effects on the onFocus event. So we
  // don't pass the autoFocus prop to the element and instead manually focus
  // the element when it's mounted. The order in which this effect runs also
  // matters. It must be declared here after all the event callbacks above so
  // the event callback effects run before this one. See
  // https://twitter.com/diegohaz/status/1408180632933388289

  useSafeLayoutEffect(function () {
    if (!focusable) return;

    if (autoFocus) {
      var _ref$current;

      (_ref$current = ref.current) == null ? void 0 : _ref$current.focus();
    }
  }, [focusable, autoFocus]);
  var tagName = useTagName(ref, props.as);
  var nativeTabbable = focusable && isNativeTabbable(tagName);
  var supportsDisabled = focusable && supportsDisabledAttribute(tagName);
  var style = trulyDisabled ? _objectSpread({
    pointerEvents: "none"
  }, props.style) : props.style;
  props = _objectSpread(_objectSpread({
    "data-focus-visible": focusable && focusVisible ? "" : undefined,
    "aria-disabled": disabled ? true : undefined
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    style: style,
    tabIndex: getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, props.tabIndex),
    disabled: supportsDisabled && trulyDisabled ? true : undefined,
    // TODO: Test Focusable contentEditable.
    contentEditable: disabled ? undefined : props.contentEditable,
    onKeyPressCapture: onKeyPressCapture,
    onClickCapture: onClickCapture,
    onMouseDownCapture: onMouseDownCapture,
    onMouseDown: onMouseDown,
    onKeyDownCapture: onKeyDownCapture,
    onFocusCapture: onFocusCapture,
    onBlur: onBlur
  });
  return props;
});
/**
 * A component that renders an element that can be focused.
 * @see https://ariakit.org/components/focusable
 * @example
 * ```jsx
 * <Focusable>Focusable</Focusable>
 * ```
 */

var Focusable = createComponent(function (props) {
  props = useFocusable(props);
  return createElement("div", props);
});
export { Focusable, useFocusable };